import type { IQuestion } from '@repo/types/questions/Question.interface';
import type {
  AnswerQuestionLong,
  AnswerUserQuestionLong,
  ExportAnswerLong,
  FeedbackAnswerQuestionLong,
  QuestionLongContent,
  QuestionLongData,
  QuestionLongSerialized,
} from '@repo/types/questions/QuestionLong';
import type { CorrectType } from '@repo/types/quiz';

import { QuestionType } from '@repo/types/enums/questionType';

export class QuestionLong implements IQuestion {
  readonly type: QuestionType;
  readonly content: string;
  private readonly points: number = 0;
  private answer: AnswerQuestionLong = null;
  private selectedAnswer: AnswerUserQuestionLong | null = null;
  private marked: FeedbackAnswerQuestionLong | null = null;
  private minLength?: number;
  private maxLength?: number;

  constructor(data: QuestionLongData, selectedAnswer: AnswerUserQuestionLong | null, marked: FeedbackAnswerQuestionLong | null) {
    this.type = QuestionType.LONG;
    this.content = data.content;
    this.points = data.points;
    this.answer = data.answer;
    this.minLength = data.minLength;
    this.maxLength = data.maxLength;
    this.selectedAnswer = selectedAnswer;
    this.marked = marked;
  }

  getContent(): QuestionLongContent {
    return {
      content: this.content,
      answer: this.answer,
      minLength: this.minLength,
      maxLength: this.maxLength,
    };
  }

  getMaxPoints(): number {
    return this.points;
  }

  getPoints(): number {
    if (this.marked == null || this.marked.points == null) return 0;

    return this.marked.points;
  }

  setAnswer(answer: string): void {
    this.selectedAnswer = { answer: answer };
  }

  getAnswer(): AnswerUserQuestionLong | null {
    return this.selectedAnswer;
  }

  isAnswered(): boolean {
    return this.selectedAnswer != null;
  }

  resetAnswer(): void {
    this.selectedAnswer = null;
  }

  setMarked(marked: FeedbackAnswerQuestionLong | null): void {
    this.marked = marked;
  }

  getMarked(): FeedbackAnswerQuestionLong | null {
    return this.marked;
  }

  isMarked(): boolean {
    return !(this.marked == null);
  }

  correctState(): CorrectType {
    if (!this.isAnswered()) return null;

    const marked = this.getMarked();
    /*
      Written answers are always graded by hand, so an answer without feedback
      yet is awaiting a grade — not incorrect.
    */
    if (marked == null) return 'waiting';
    if (marked.manualCheck && !marked.marked) return 'waiting';

    const points: number = this.getPoints();
    const maxPoints: number = this.getMaxPoints();

    if (points === 0) return false;
    return points === maxPoints ? true : 'partially';
  }

  toJSON(): QuestionLongSerialized {
    return {
      marked: this.marked,
      selectedAnswer: this.selectedAnswer,
    };
  }

  importAnswer(answer: ExportAnswerLong): void {
    if (answer.answer) {
      this.selectedAnswer = { answer: answer.answer };
    } else {
      this.selectedAnswer = null;
    }
  }

  exportAnswer(): ExportAnswerLong {
    return { answer: this.selectedAnswer?.answer || null };
  }
}
